import React, { useMemo } from "react";
import { useField } from "formik";

import { Dropdown } from "react-bootstrap";
import styles from "../RuleForm.module.scss";
import { useWorkspaceRuleGroups } from "../../../../../api/rules.api";
import { CustomSelect } from "../../../../forms/CustomSelect/CustomSelect";
import { ManagementDropdown } from "../../../../general/ManagementDropdown/ManagementDropdown";
import {
    AccountingRuleFormActions,
    BusinessRuleFormActions,
    FormRuleActionType,
    TransactionRuleActionFormSchema,
} from "../constants";
import { TransactionRuleGroup } from "../../../../../common/types/transactionRule";
import { EntityActionForm } from "./EntityActionForm";
import { CategoryActionForm } from "./CategoryActionForm";
import { AccountActionForm } from "./AccountActionForm";
import { MemoActionForm } from "./MemoActionForm";
import { CounterpartyActionForm } from "./CounterpartyActionForm";
import { ClassActionForm } from "./ClassActionForm";

interface Props {
    index: number;
    id: string;
    onRemove: (index: number) => void;
}

export const RuleActionForm: React.FC<Props> = ({ index, id, onRemove }) => {
    const ruleGroups = useWorkspaceRuleGroups();
    const [{ value: groupId }] = useField<string | undefined>("groupId");
    const [{ value: allActions }] =
        useField<TransactionRuleActionFormSchema[]>("actions");
    const [actionTypeField, , actionTypeHelpers] = useField<FormRuleActionType>(
        `actions.${index}.actionType`,
    );

    const canRemove = allActions.length > 1;
    const actionType = actionTypeField.value;

    const selectedTypes = useMemo(
        () =>
            allActions
                .map((a) => a.actionType)
                .filter((v): v is FormRuleActionType => v !== undefined),
        [allActions],
    );

    const selectedGroup = useMemo(() => {
        const groups = ruleGroups.data?.ruleGroups as
            | TransactionRuleGroup[]
            | undefined;
        return groups?.find((g) => g.id === groupId);
    }, [ruleGroups.data?.ruleGroups, groupId]);

    const actionTypesOptions = useMemo(() => {
        const actions = selectedGroup?.isAccounting
            ? AccountingRuleFormActions
            : BusinessRuleFormActions;

        return actions
            .filter((v) => !selectedTypes.includes(v) || v === actionType)
            .map((value) => ({ value }));
    }, [selectedGroup?.isAccounting, actionType, selectedTypes]);

    return (
        <>
            <div className={styles.sectionRowContent}>
                <CustomSelect
                    value={actionType}
                    onSelected={(value: string) =>
                        actionTypeHelpers.setValue(value as FormRuleActionType)
                    }
                    dropdownKey={`actionType${id}`}
                    placeholder="Select action type"
                    size="xs"
                    empty="No available actions"
                    options={actionTypesOptions}
                />

                {actionType === FormRuleActionType.CATEGORY && (
                    <CategoryActionForm index={index} />
                )}

                {actionType === FormRuleActionType.ENTITY && (
                    <EntityActionForm index={index} />
                )}

                {actionType === FormRuleActionType.ACCOUNT_ID && (
                    <AccountActionForm index={index} />
                )}
                {actionType === FormRuleActionType.ACCOUNT_NAME && (
                    <AccountActionForm index={index} />
                )}

                {actionType === FormRuleActionType.MEMO && (
                    <MemoActionForm index={index} />
                )}

                {actionType === FormRuleActionType.COUNTERPARTY && (
                    <CounterpartyActionForm index={index} />
                )}

                {actionType === FormRuleActionType.CLASS && (
                    <ClassActionForm index={index} />
                )}
            </div>

            {canRemove && (
                <ManagementDropdown
                    id={`actionManagement${index}`}
                    className={styles.conditionManagementDropdown}
                >
                    <Dropdown.Item
                        onClick={() => onRemove(index)}
                        className="text-danger"
                    >
                        Remove
                    </Dropdown.Item>
                </ManagementDropdown>
            )}
        </>
    );
};
